import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "components/Button";
import { Home, Search } from "lucide-react";

const NotFound: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen w-full bg-[#FCFAFF] flex flex-col items-center justify-center p-4 overflow-hidden relative">
      {/* Background blobs for premium feel */}
      <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] bg-purple-100 rounded-full blur-[100px] opacity-50" />
      <div className="absolute bottom-[-10%] right-[-5%] w-[40%] h-[40%] bg-indigo-100 rounded-full blur-[100px] opacity-50" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="z-10 text-center"
      >
        <motion.div
          animate={{
            y: [0, -20, 0],
            rotate: [0, 5, -5, 0]
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="mb-8 relative inline-block"
        >
          <div className="text-[12rem] font-bold text-purple-900/5 select-none leading-none">
            404
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
             <Search className="w-24 h-24 text-purple-600 opacity-80" />
          </div>
        </motion.div>

        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
          Page Not Found
        </h1>

        <p className="text-lg text-gray-600 mb-10 max-w-md mx-auto leading-relaxed">
          The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.
        </p>

        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="inline-block"
        >
          <Button
            onClick={() => navigate("/")}
            size="3xl"
            className="rounded-2xl px-10 font-semibold shadow-lg hover:shadow-purple-200/50 transition-shadow"
            leftIcon={<Home className="w-5 h-5 mr-2" />}
          >
            Back to Home
          </Button>
        </motion.div>
      </motion.div>

      {/* Decorative elements */}
      <motion.div
        className="absolute top-20 right-20 w-4 h-4 bg-purple-400 rounded-full"
        animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-40 left-20 w-3 h-3 bg-indigo-400 rounded-full"
        animate={{ scale: [1, 2, 1], opacity: [0.2, 0.5, 0.2] }}
        transition={{ duration: 4, repeat: Infinity, delay: 1 }}
      />
    </div>
  );
};

export default NotFound;
